import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CommonFilter } from "../../Components/Common/CommonFilter";
import { getActiveThreadTypesArr, getThreadTypesArr, getUsersArr } from "../../Store/Reducers";
import { MdPhone } from 'react-icons/md';
import { BsWhatsapp } from 'react-icons/bs';
import { timeAgo, getTaskColor, getColorClass, getLastDateColor, FormatAmount } from '../../Utils/Utilities';
import { getTableDateOrWeekday } from "../../Utils/DateTime";
import ThrDetails from './ThrDetails';
import { useCurrency } from "../../Hooks/useCurrency";
import { TooltipProvider } from "../../Components/UI/tooltip";
import { Search } from 'lucide-react';
import { Input } from '../../Components/UI/input';
import { Button } from '../../Components/UI/button';
import { cn } from '../../Lib/Cn';
import { handlePhoneClickAction, handleWhatsAppClickAction } from "../../Utils/ContactLogUtils";


const commitmentCategories = [
	{ id: 'overdue', name: 'Overdue' },
	{ id: 'today', name: 'Today' },
	{ id: 'tomorrow', name: 'Tomorrow' },
	{ id: 'thisweek', name: 'This Week' },
	{ id: 'future', name: 'Future' }
];

const excludedTypeIDs = [1, 4];

const CommitmentView = () => {
	const currencySymbol = useCurrency();

	const data = useSelector((state) => state.threadData.data);
	const threadTypes = useSelector(getActiveThreadTypesArr);
	const users = useSelector(getUsersArr);
	const tasksData = useSelector((state) => state.taskData.data) || [];

	const [selectedFilters, setSelectedFilters] = useState({ types: "" });
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedThread, setSelectedThread] = useState({});
	const [isThreadDetailsOpen, setIsThreadDetailsOpen] = useState(false);
	const { data: connectionData } = useSelector((state) => state.connectionData);

	const stageRefs = useRef({});

	// users lookup
	const usersMap = useMemo(() => {
		return users.reduce((acc, user) => {
			acc[user.userID] = user.name;
			return acc;
		}, {});
	}, [users]);

	const getExecutiveName = useCallback((userID) => {
		if (!userID) return "-";
		return usersMap[Number(userID)] || "-";
	}, [usersMap]);

	// date categorization
	const categorizeDueDate = useCallback((dueDT) => {
		if (!dueDT) return 'future';

		const dueDate = new Date(dueDT);
		const today = new Date();

		// Reset time for accurate comparison
		const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
		const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());

		const diffTime = dueDateStart.getTime() - todayStart.getTime();
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

		if (diffDays < 0) return 'overdue';
		if (diffDays === 0) return 'today';
		if (diffDays === 1) return 'tomorrow';
		if (diffDays <= 7 - today.getDay()) return 'thisweek';
		return 'future';
	}, []);

	// filtered thread types
	const filteredThreadTypes = useMemo(() =>
		threadTypes.filter(t => !excludedTypeIDs.includes(t.TID)),
		[threadTypes]
	);

	// Main data processing 
	const processedAndFilteredThreads = useMemo(() => {
		if (!Array.isArray(data) || data.length === 0) return [];

		const term = searchTerm.trim().toLowerCase();

		// Single pass processing with all filters
		const processed = data
			.filter(thread => {
				if (excludedTypeIDs.includes(thread.TypeID)) return false;

				// Exclude threads from cold contacts
				const threadConnection = connectionData?.find(conn => conn.ConnID === thread.ConnID);
				if (threadConnection && (threadConnection.IsCold === 1 || threadConnection.IsCold === true)) {
					return false;
				}

				// Type filter
				if (selectedFilters.types && selectedFilters.types !== "") {
					if (!selectedFilters.types.toString().includes(thread.TypeID.toString())) {
						return false;
					}
				}

				return true;
			})
			.map(item => {
				// Process connection data (keep for other fields)
				const currentConn = {
					bizName: item.bizName ?? item.connectionData?.BizName,
					contactName: item.contactName ?? item.connectionData?.ContactName,
					cc: item.cc ?? item.connectionData?.CC,
					mobile: item.mobile ?? item.connectionData?.Mobile,
					email: item.email ?? item.connectionData?.Email,
					lastRecDT: item.lastRec ?? item.connectionData?.LastRecDT,
				};

				// Get tasks for this thread from tasksData
				const currThreadTasks = tasksData.filter((task) => {
					if (!task.ThreadIDs) return false;
					const threadIDs = task.ThreadIDs.split(",").map((id) => id.trim());
					return threadIDs.includes(item.ThreadID.toString());
				});

				// Find next task (earliest DueDT)
				let nextTask = null;
				for (const task of currThreadTasks) {
					if (!task?.DueDT) continue;
					if (!nextTask || task.DueDT < nextTask.DueDT) {
						nextTask = task;
					}
				}

				const pocUserName = getExecutiveName(item.POCUserID);
				const dueDTCategory = categorizeDueDate(nextTask?.DueDT);
				const dueDTF = getTableDateOrWeekday(nextTask?.DueDT);
				const nextTaskTyp = nextTask?.TaskType || "";

				const processedThread = {
					threadID: item.ThreadID,
					typeID: item.TypeID,
					agencyID: item.AgencyID,
					connID: item.ConnID,
					mainProduct: item.MainProduct,
					mainQty: item.MainQty,
					model: item.Model,
					pocUserID: item.POCUserID,
					pocUserName,
					statusID: item.StatusID,
					dueDT: nextTask?.DueDT,
					dueDTF,
					dueDTColor: getColorClass(getTaskColor(nextTask?.DueDT)),
					dueDTCategory,
					sinceDT: item.SinceDT,
					sinceDTF: getTableDateOrWeekday(item.SinceDT),
					reference: item.Reference,
					bizProspect: item.BizProspect,
					currStageID: item.CurrStageID,
					nextTaskDT: nextTask?.DueDT,
					source: item.Source,
					nextTaskDTColor: getTaskColor(nextTask?.DueDT),
					nextTaskDTF: getTableDateOrWeekday(nextTask?.DueDT),
					nextTaskNote: nextTask?.Description,
					nextTaskTyp,
					lastRecordDT: currentConn?.lastRecDT,
					lastRecordDTColor: getLastDateColor(currentConn?.lastRecDT),
					lastRecordDTF: getTableDateOrWeekday(currentConn?.lastRecDT),
					lastRecordDTAgo: timeAgo(currentConn?.lastRecDT),
					notes: item.Notes,
					connectionData: {
						bizName: currentConn?.bizName,
						contactName: currentConn?.contactName,
						cc: currentConn?.cc,
						mobile: currentConn?.mobile,
						email: currentConn?.email,
						tasks: currThreadTasks.map(task => ({
							TIDs: task.ThreadIDs,
							DT: task.DueDT,
							Txt: task.Description,
							Typ: task.TaskType,
							Pri: task.Priority,
						})),
					},
					feedbackNotes: item.FdbkNotes,
					feedbackScore: item.FdbkScore,
					failureReason: item.FailureReason,
				};

				// Apply search filter if term exists
				if (term) {
					if (term === "0") {
						return Number(processedThread.bizProspect) === 0 ? processedThread : null;
					}

					const fieldsToSearch = [
						processedThread.pocUserName,
						processedThread.mainProduct,
						processedThread.connectionData?.bizName,
						processedThread.bizProspect?.toString(),
						nextTaskTyp,
						processedThread.nextTaskNote,
						processedThread.source,
						processedThread.Source,
						processedThread.src,
						processedThread.Src,
					];

					const matchesSearch = fieldsToSearch
						.filter(Boolean)
						.some((field) => field.toString().toLowerCase().includes(term));

					return matchesSearch ? processedThread : null;
				}

				return processedThread;
			})
			.filter(Boolean);

		return processed;
	}, [data, searchTerm, selectedFilters.types, users, tasksData]);

	// Auto-select thread type with most threads on initial load
	useEffect(() => {
		if (data && data.length > 0 && selectedFilters.types === "") {

			const typeCounts = {};
			data.forEach(thread => {
				if (!excludedTypeIDs.includes(thread.TypeID)) {
					typeCounts[thread.TypeID] = (typeCounts[thread.TypeID] || 0) + 1;
				}
			});

			// Find most common type
			const typeCountsArray = Object.entries(typeCounts);
			if (typeCountsArray.length > 0) {
				const mostCommonType = typeCountsArray.reduce((a, b) =>
					a[1] > b[1] ? a : b
				)[0];

				setSelectedFilters(prev => ({ ...prev, types: mostCommonType.toString() }));
			}
		}
	}, [data, selectedFilters.types]);

	// threads by category
	const threadsByCategory = useMemo(() => {
		if (!processedAndFilteredThreads.length) return {};

		return commitmentCategories.reduce((acc, category) => {
			acc[category.id] = processedAndFilteredThreads.filter(thread =>
				thread.dueDTCategory === category.id
			);
			return acc;
		}, {});
	}, [processedAndFilteredThreads]);

	// category totals for navigation
	const categoryTotals = useMemo(() => {
		return Object.entries(threadsByCategory).reduce((acc, [categoryId, threads]) => {
			acc[categoryId] = threads.reduce((sum, thread) => sum + Number(thread.bizProspect || 0), 0);
			return acc;
		}, {});
	}, [threadsByCategory]);

	// Event handlers
	const handleRowClick = useCallback((thread) => {
		setSelectedThread(thread);
		setIsThreadDetailsOpen(true);
	}, []);

	const handleFilterChange = useCallback((value) => {
		setSelectedFilters(prev => ({ ...prev, types: value }));
		if (!value || value === "all") {
			setSearchTerm("");
		}
	}, []);

	const handleSearchChange = useCallback((e) => {
		setSearchTerm(e.target.value);
	}, []);

	const scrollToCategory = useCallback((categoryId) => {
		stageRefs.current[categoryId]?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
	}, []);

	//   ThreadCard component
	const ThreadCard = useMemo(() => {
		return ({ thread }) => (
			<div
				className="bg-gray-100 sahi-radius shadow-sm border border-gray-200 f-col justify-between hover:cursor-pointer"
				onClick={() => handleRowClick(thread)}
			>
				{/* Header Section */}
				<div className="f-col gap-1 p-3 pt-4">
					<div className="f-row justify-between gap-1">
						<div className="f-col items-start justify-start flex-1 min-w-0 max-w-[65%]">
							<div className="f-col min-w-0 gap-1 max-w-full">
								<p className="font-semibold text-text-color text-md truncate max-w-full">
									{thread.connectionData?.bizName?.length > 25
										? `${thread.connectionData.bizName.slice(0, 25)}...`
										: thread.connectionData?.bizName || "BizName"}
								</p>
								<p className="text-sm text-sub-text-color mt-1 truncate">
									{thread.pocUserID ? thread.pocUserName : "POC: Not Assigned"}
								</p>
								{thread.mainProduct && (
									<p className="text-sm text-sub-text-color mt-1 truncate">
										{thread.mainProduct}
									</p>
								)}
							</div>
						</div>

						<div className="f-col gap-2 justify-center items-end text-right shrink-0 max-w-[35%]">
							<span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis font-semibold text-sub-text-color text-right">
								<FormatAmount
									amount={thread.bizProspect}
									currency={currencySymbol}
									showCurr={true}
								/>
							</span>
							<span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis text-sub-text-color text-right">
								Recd: <strong>{thread.sinceDTF || "-"}</strong>
							</span>
							{thread.dueDT && (
								<span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis text-sub-text-color text-right">
									Due: <strong className={thread.dueDTColor}>{thread.dueDTF}</strong>
								</span>
							)}
						</div>
					</div>
				</div>

				{/* Footer Section */}
				<div className="flex justify-between items-center text-sm border-t bg-[#fcfcfc] rounded-b-md border-100 w-full p-2 px-3">
					<div className="flex flex-wrap gap-3 items-center">
						<span className="whitespace-nowrap text-sub-text-color">
							Next:{" "}
							{thread.nextTaskDT ? (
								<>
									<strong style={{ color: thread.nextTaskDTColor }}>
										{thread.nextTaskDTF}
									</strong>
									{thread.nextTaskTyp && ` (${thread.nextTaskTyp})`}
								</>
							) : (
								"Not Set"
							)}
						</span>
					</div>

					<div className="flex gap-2 text-lg justify-end">
						{thread.connectionData?.mobile && (
							<>
								<button
									className="h-6 w-6 flex items-center justify-center rounded-md hover:shadow-sm cursor-pointer transform-gpu transition-transform duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-blue-light"
									onClick={(e) => {
										e.stopPropagation();
										const isDesktop = typeof window !== 'undefined' && window.innerWidth > 768;
										handlePhoneClickAction(thread.connectionData, isDesktop, null, thread.connID);
									}}
								>
									<MdPhone className="w-4.5 h-4.5 text-black" />
								</button>
								<button
									className="h-6 w-6 flex items-center justify-center rounded-md hover:shadow-sm cursor-pointer transform-gpu transition-transform duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-green-light"
									onClick={(e) => {
										e.stopPropagation();
										handleWhatsAppClickAction(thread.connectionData, null, thread.connID);
									}}
								>
									<BsWhatsapp className="w-4.5 h-4.5 text-sahi-green" />
								</button>
							</>
						)}
					</div>
				</div>
			</div>
		);
	}, [handleRowClick, currencySymbol]);

	// CategoryColumn component
	const CategoryColumn = useMemo(() => {
		return ({ category, threads }) => (
			<div className="w-full flex flex-col">
				{/* Header */}
				<div className=" px-4 py-3 pl-0 flex justify-between items-start">
					<h3 className="font-semibold text-text-color text-xl">
						{category.name}{" "}
						<span className="text-text-color text-xl font-normal">
							{threads.length > 0 && `(${threads.length})`}
						</span>
					</h3>
				</div>

				{/* Content */}
				<div className={`shadow-sm sahi-radius transition-colors duration-200 bg-white ${threads.length === 0 ? "p-2 w-fit" : "p-4"
					}`}>
					{threads.length === 0 ? (
						<div className="text-sub-text-color">
							{(() => {
								const selectedThreadType = filteredThreadTypes.find(
									(t) => t.TID.toString() === selectedFilters.types
								);

								return selectedThreadType
									? `No ${selectedThreadType.Typ}.`
									: `No threads in ${category.name.toLowerCase()}`;
							})()}
						</div>
					) : (
						<div
							className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 w-full"
							style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}
						>
							{threads.map((thread) => (
								<ThreadCard key={thread.threadID} thread={thread} />
							))}
						</div>
					)}
				</div>
			</div>
		);
	}, [ThreadCard, filteredThreadTypes, selectedFilters.types]);

	const showContent = selectedFilters.types && selectedFilters.types !== "all";
	const showSearch = showContent;

	return (
		<TooltipProvider>
			<div className="space-y-3 lg:space-y-6 overflow-x-hidden">
				{/* Header */}
				<div className="hidden lg:flex justify-between items-center gap-3">
					<div className="flex items-center gap-4 justify-between w-full">
						<h1 className="text-3xl font-bold text-text-color web-header-name">
							Commitment View
						</h1>
					</div>

					{showSearch && (
						<div className="flex items-center justify-end flex-grow">
							<div className="relative filter-border">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
								<Input
									placeholder="Search"
									value={searchTerm}
									onChange={handleSearchChange}
									className="pl-10 h-10 w-80 border-none focus:outline-none focus:ring-0 shadow-none"
								/>
							</div>
						</div>
					)}
				</div>

				{/* Filter + Category Navigation */}
				<div className="hidden lg:flex items-center justify-between gap-6">
					{/* Thread Type Filter */}
					<CommonFilter
						label="Select Thread Type"
						options={filteredThreadTypes.map((t) => ({
							name: t.Typ,
							ID: t.TID.toString(),
						}))}
						value={selectedFilters.types}
						onChange={handleFilterChange}
						searchEnabled={true}
						displayValue={(val) => {
							if (!val || val === "") return "All Thread Types";
							const selectedType = filteredThreadTypes.find(
								(t) => t.TID.toString() === val
							);
							return selectedType ? selectedType.Typ : "All Thread Types";
						}}
						showAllOption={true}
						showAllOptionNum={true}
					/>

					{/* Category Navigation */}
					{showContent && (
						<div className="flex gap-4 overflow-x-auto border-gray-200 flex-1">
							{commitmentCategories.map((category) => (
								<Button
									key={category.id}
									onClick={() => scrollToCategory(category.id)}
									variant="semidefaultcol"
									className={cn(
										"w-auto bg-white text-black sahi-radius px-3 py-2",
										"filter-border",
										"flex flex-row items-center align-middle cursor-pointer"
									)}
								>
									<label className="inline-flex items-center gap-1 cursor-pointer">
										<div className="ml-1 flex flex-row items-center gap-1">
											<span className="flex items-center text-sm font-normal text-black gap-1">
												{category.name}
												<span className="flex items-center text-sm text-text-color font-semibold">
													<span className="text-xs text-sub-text-color font-medium">(</span>
													{categoryTotals[category.id] === 0 ? (
														`${currencySymbol} 0`
													) : (
														<FormatAmount
															amtClassName="text-sm font-semibold text-text-color"
															amount={categoryTotals[category.id]}
															currency={currencySymbol}
															showCurr={true}
														/>
													)}
													<span className="text-xs text-sub-text-color font-medium ml-0.5">)</span>
												</span>
											</span>
										</div>
									</label>
								</Button>
							))}
						</div>
					)}
				</div>

				{/* Commitment View Content */}
				<div className="w-full space-y-6">
					{!showContent ? (
						<div>
							<p className="no-records border border-gray-200 shadow-sm sahi-radius">
								Please select a thread type to view categories.
							</p>
						</div>
					) : (
						commitmentCategories.map((category) => (
							<div
								key={category.id}
								ref={(el) => (stageRefs.current[category.id] = el)}
							>
								<CategoryColumn
									category={category}
									threads={threadsByCategory[category.id] || []}
								/>
							</div>
						))
					)}
				</div>

				<ThrDetails
					isOpen={isThreadDetailsOpen}
					onClose={() => setIsThreadDetailsOpen(false)}
					threadID={selectedThread?.threadID}
					initialData={selectedThread}
				/>
			</div>
		</TooltipProvider>
	);
};

export default CommitmentView;